import { AuthInfo, UserInfo } from '../types/auth';

/**
 * Azure Static Web Apps Authentication Service
 */
class AuthService {
  private static AUTH_ENDPOINT = '/.auth/me';
  private static LOGIN_ENDPOINT = '/.auth/login/github';
  private static LOGOUT_ENDPOINT = '/.auth/logout';

  /**
   * Get current user authentication information
   */
  static async getUserInfo(): Promise<UserInfo> {
    try {
      const response = await fetch(this.AUTH_ENDPOINT);
      
      if (!response.ok) {
        console.warn('Failed to fetch user info:', response.status);
        return this.getUnauthenticatedUser();
      }

      const authInfo: AuthInfo = await response.json();
      
      if (!authInfo.clientPrincipal) {
        return this.getUnauthenticatedUser();
      }

      const { clientPrincipal } = authInfo;
      const roles = clientPrincipal.userRoles || [];

      return {
        isAuthenticated: true,
        userId: clientPrincipal.userId,
        userName: clientPrincipal.userDetails,
        roles,
        isAdmin: roles.includes('administrator'),
        isContributor: roles.includes('contributor') || roles.includes('administrator'),
      };
    } catch (error) {
      console.error('Error fetching user info:', error);
      return this.getUnauthenticatedUser();
    }
  }

  /**
   * Check if user has a specific role
   */
  static async hasRole(role: string): Promise<boolean> {
    const userInfo = await this.getUserInfo();
    return userInfo.roles.includes(role);
  }

  /**
   * Check if user is authenticated
   */
  static async isAuthenticated(): Promise<boolean> {
    const userInfo = await this.getUserInfo();
    return userInfo.isAuthenticated;
  }

  /**
   * Login with Azure AD
   */
  static login(redirectUrl: string = '/admin'): void {
    window.location.href = `${this.LOGIN_ENDPOINT}?post_login_redirect_uri=${encodeURIComponent(redirectUrl)}`;
  }

  /**
   * Logout
   */
  static logout(redirectUrl: string = '/'): void {
    window.location.href = `${this.LOGOUT_ENDPOINT}?post_logout_redirect_uri=${encodeURIComponent(redirectUrl)}`;
  }

  /**
   * Get unauthenticated user object
   */
  private static getUnauthenticatedUser(): UserInfo {
    return {
      isAuthenticated: false,
      roles: [],
      isAdmin: false,
      isContributor: false,
    };
  }
}

export default AuthService;
